import { useEffect, useMemo, useRef, useState } from "react";
import { Event, DashboardSummary, useBulkSetBuybackFveIntent } from "@workspace/api-client-react";
import {
  ChevronDown,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { isEventPast, formatVenueTimeLabel } from "@/lib/event-time";
import { artistKey, groupByArtist } from "@/lib/event-grouping";
import { toast } from "sonner";

const COLLAPSE_KEY = "tex-events-sidebar-collapsed";
const GROUPS_KEY = "tex-events-sidebar-groups";
const WIDTH_KEY = "tex-events-sidebar-width";
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 200;
const MAX_WIDTH = 420;

interface EventsSidebarProps {
  events: Event[] | undefined;
  eventsLoading: boolean;
  selectedEventId: number | null;
  onSelectEvent: (id: number) => void;
  summary?: DashboardSummary;
  // True when a puller is selected and its inventory has loaded but no event
  // has remaining inventory for it — show a puller-specific empty message.
  pullerHasNoInventory?: boolean;
}

export function EventsSidebar({
  events,
  eventsLoading,
  selectedEventId,
  onSelectEvent,
  summary,
  pullerHasNoInventory = false,
}: EventsSidebarProps) {
  const isMobile = useIsMobile();
  const { mutate: bulkIntent } = useBulkSetBuybackFveIntent();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === "true";
  });
  // On phones the full-width sidebar would crowd out the inventory cards, so
  // collapse to the compact avatar rail the first time we enter a mobile
  // viewport. Operators can still expand it manually afterwards.
  const wasMobile = useRef(false);
  useEffect(() => {
    if (isMobile && !wasMobile.current) setCollapsed(true);
    wasMobile.current = isMobile;
  }, [isMobile]);
  const [search, setSearch] = useState("");
  const [showPast, setShowPast] = useState(false);
  // User-resizable width for the expanded sidebar, persisted across reloads.
  // The collapsed avatar rail keeps its fixed narrow width and ignores this.
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_WIDTH;
    const v = Number(window.localStorage.getItem(WIDTH_KEY));
    return v >= MIN_WIDTH && v <= MAX_WIDTH ? v : DEFAULT_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  // Per-artist collapse state, keyed by artist name. Absence means expanded
  // (groups are expanded by default); a `true` value means collapsed.
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(
    () => {
      if (typeof window === "undefined") return {};
      try {
        return JSON.parse(window.localStorage.getItem(GROUPS_KEY) || "{}");
      } catch {
        return {};
      }
    },
  );

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    window.localStorage.setItem(GROUPS_KEY, JSON.stringify(collapsedGroups));
  }, [collapsedGroups]);

  useEffect(() => {
    window.localStorage.setItem(WIDTH_KEY, String(width));
  }, [width]);

  // Drag the right-edge handle to resize; listeners live on window so the drag
  // keeps tracking even if the pointer leaves the thin handle. Double-click the
  // handle to reset to the default width.
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    // Capture the pointer so a release that lands outside the window (or a
    // cancelled touch drag) still ends the drag; without this the sidebar could
    // stay stuck in the dragging state with the body cursor locked.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: PointerEvent) => {
      const next = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX)),
      );
      setWidth(next);
    };
    const onUp = () => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const toggleGroup = (artist: string) =>
    setCollapsedGroups((prev) => ({ ...prev, [artist]: !prev[artist] }));

  // Only enabled events appear in the Workspace. Disabled events are paused
  // (no background sync, excluded from the global G/B/H totals); they return to
  // the sidebar when re-enabled in Settings or auto-enabled as their date nears.
  // Settings and pricing render their own lists off the same hook, so filtering
  // here does not affect them.
  const allEvents = (events ?? []).filter((e) => e.enabled);
  const totalEvents = allEvents.length;

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allEvents;
    return allEvents.filter((e) =>
      [e.label, e.performer, e.venue]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q)),
    );
  }, [allEvents, search]);

  // Split into upcoming (no date, or venue-local datetime in the future) vs.
  // past (venue-local datetime already elapsed). The comparison uses the event's
  // venue timezone so an event "today" stays upcoming until it has actually
  // started in its own city, regardless of the viewer's browser timezone. Past
  // events are archived behind a toggle.
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = new Date();
    const upcoming: Event[] = [];
    const past: Event[] = [];
    for (const e of filteredEvents) {
      if (isEventPast(e.eventDate, e.eventTimezone, now)) past.push(e);
      else upcoming.push(e);
    }
    return { upcomingEvents: upcoming, pastEvents: past };
  }, [filteredEvents]);

  // Only nest under artist headers when more than one artist is visible.
  const showHeaders = useMemo(() => {
    const artists = new Set(filteredEvents.map(artistKey));
    return artists.size > 1;
  }, [filteredEvents]);

  const searchActive = search.trim() !== "";
  const countLabel = searchActive
    ? `${filteredEvents.length} of ${totalEvents}`
    : `${totalEvents} ${totalEvents === 1 ? "event" : "events"}`;

  function stampIntent(eventId: number, intent: "buy_back" | "sell", label: string) {
    bulkIntent(
      { id: eventId, data: { intent, ids: [] } },
      {
        onSuccess(data) {
          const n = data.updated;
          toast.success(
            n === 0
              ? "No eligible listings to update."
              : `${n} listing${n === 1 ? "" : "s"} set to ${label}.`,
          );
        },
        onError() {
          toast.error(`Failed to stamp listings. Please try again.`);
        },
      },
    );
  }

  const renderEventRow = (event: Event) => {
    const active = selectedEventId === event.id;
    const monthDay = formatMonthDay(event.eventDate);
    // Venue-local start time (e.g. "8:00 PM EDT") when the event has a time
    // portion; null for date-only events so the row shows the date alone.
    const timeLabel = formatVenueTimeLabel(event.eventDate, event.eventTimezone);
    const remaining = event.remainingTicketCount;
    // Rows with no inventory left are de-emphasized so shows that still have
    // tickets lead the eye; the active row always stays at full strength.
    const soldOut = remaining === 0;
    const bbEnabled = !!event.buybacksEnabled;
    const showBbMenu = bbEnabled;
    return (
      <ContextMenu key={event.id}>
        <ContextMenuTrigger asChild>
          <button
            onClick={() => onSelectEvent(event.id)}
            aria-current={active ? "true" : undefined}
            className={cn(
              "w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-secondary border-border shadow-sm"
                : "bg-transparent border-transparent hover:bg-muted hover:border-border",
              soldOut && !active && "opacity-60",
            )}
          >
            <span className="shrink-0 w-[4.25rem] flex flex-col leading-tight">
              <span className="text-[10px] font-mono font-medium text-muted-foreground">
                {monthDay ?? "—"}
              </span>
              {timeLabel && (
                <span className="text-[9px] font-mono text-muted-foreground/70 truncate">
                  {timeLabel}
                </span>
              )}
            </span>
            <span className="flex-1 truncate text-xs text-foreground">
              {event.venue || event.label}
            </span>
            {remaining !== undefined &&
              (remaining > 0 ? (
                <span className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-mono font-medium text-primary tabular-nums">
                  {remaining}
                </span>
              ) : (
                <span className="shrink-0 px-1.5 text-[10px] font-mono text-muted-foreground/40 tabular-nums">
                  0
                </span>
              ))}
          </button>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          {showBbMenu && (
            <>
              <ContextMenuItem
                onSelect={() => stampIntent(event.id, "buy_back", "Buy Back")}
              >
                Set all Eligible → Buy Back
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}
          <ContextMenuItem
            onSelect={() => stampIntent(event.id, "sell", "Sell")}
          >
            Set all Eligible → Sell
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  const renderGroups = (list: Event[]) => {
    if (!showHeaders) {
      return <div className="space-y-0.5">{list.map(renderEventRow)}</div>;
    }
    return groupByArtist(list).map((group) => {
      const isCollapsed = !!collapsedGroups[group.artist];
      return (
        <div key={group.artist} className="space-y-0.5">
          <button
            onClick={() => toggleGroup(group.artist)}
            aria-expanded={!isCollapsed}
            className="w-full flex items-center gap-1 px-1 py-1 rounded-md text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 transition-transform",
                isCollapsed && "-rotate-90",
              )}
            />
            <span className="truncate">{group.artist}</span>
          </button>
          {!isCollapsed && (
            <div className="space-y-0.5 pl-1.5">
              {group.events.map(renderEventRow)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <aside
      className={cn(
        "relative border-r border-border bg-card flex flex-col h-full shrink-0 overflow-hidden",
        !dragging && "transition-[width] duration-200 ease-in-out",
        collapsed && "w-12",
      )}
      style={collapsed ? undefined : { width }}
    >
        {!collapsed && (
          <div
            onPointerDown={startResize}
            onDoubleClick={() => setWidth(DEFAULT_WIDTH)}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize events sidebar (double-click to reset)"
            title="Drag to resize · double-click to reset"
            className="group absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize"
          >
            <span
              className={cn(
                "absolute inset-y-0 right-0 w-px transition-colors",
                dragging
                  ? "bg-primary"
                  : "bg-transparent group-hover:bg-primary/50",
              )}
            />
          </div>
        )}
        {collapsed ? (
          <>
            {/* Header */}
            <div className="h-[49px] border-b border-border flex items-center justify-center shrink-0">
              <button
                onClick={() => setCollapsed(false)}
                className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Expand events sidebar"
                title="Expand"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable avatar list */}
            <div className="flex-1 overflow-y-auto py-2 px-1 space-y-1.5 custom-scrollbar">
              {eventsLoading ? (
                <div className="flex justify-center p-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              ) : (
                allEvents.map((event) => {
                  const active = selectedEventId === event.id;
                  const held = !!event.heldCount;
                  const hasInv = (event.remainingTicketCount ?? 0) > 0;
                  return (
                    <Tooltip key={event.id}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onSelectEvent(event.id)}
                          aria-label={event.label}
                          aria-current={active ? "true" : undefined}
                          className="flex flex-col items-center gap-1 w-full py-0.5 focus-visible:outline-none group"
                        >
                          <span
                            className={cn(
                              "relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition-all ring-offset-2 ring-offset-card group-focus-visible:ring-2 group-focus-visible:ring-ring",
                              active
                                ? "bg-secondary text-foreground ring-2 ring-primary shadow-sm"
                                : held
                                  ? "bg-muted text-foreground ring-2 ring-amber-500"
                                  : "bg-muted text-muted-foreground group-hover:bg-secondary group-hover:text-foreground",
                            )}
                          >
                            {initials(event.label)}
                            {hasInv && (
                              <span
                                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-card"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                          <span className="text-[8px] font-mono uppercase tracking-wide text-muted-foreground leading-none">
                            {formatPill(event.eventDate)}
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-sans">
                        <div className="text-xs font-medium">{event.label}</div>
                        <div className="flex items-center gap-2 text-[10px] font-mono mt-0.5">
                          <span className="text-green-500">{event.goodPhoneCount || 0}G</span>
                          <span className="text-red-500">{event.badPhoneCount || 0}B</span>
                          {event.heldCount ? (
                            <span className="text-amber-500">{event.heldCount}H</span>
                          ) : null}
                          {event.remainingTicketCount !== undefined && (
                            <span className="text-muted-foreground">
                              {event.remainingTicketCount} inv
                            </span>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })
              )}
            </div>

            {/* Footer: compact INV with tooltip */}
            {summary && (
              <div className="px-1.5 py-2 border-t border-border bg-panel shrink-0 flex justify-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center font-mono text-[10px] text-foreground cursor-default leading-tight">
                      <span className="text-[8px] text-muted-foreground">INV</span>
                      {summary.remainingInventory}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <GlobalStatusContent summary={summary} />
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Header */}
            <div className="h-[49px] px-3 border-b border-border flex items-center justify-between bg-card shrink-0">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Events
              </h2>
              <button
                onClick={() => setCollapsed(true)}
                className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Collapse events sidebar"
                title="Collapse"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>

            {/* Search */}
            <div className="p-2 border-b border-border bg-card shrink-0">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Filter events"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-7 text-xs pl-7 pr-7"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear event filter"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable event list */}
            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
              {eventsLoading ? (
                <div className="flex justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              ) : totalEvents === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  {pullerHasNoInventory
                    ? "No inventory found for this puller."
                    : "No events configured."}
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No events match "{search}".
                </div>
              ) : (
                <div className="space-y-1">
                  {renderGroups(upcomingEvents)}

                  {pastEvents.length > 0 && (
                    <div className="pt-1">
                      <button
                        onClick={() => setShowPast((s) => !s)}
                        className="w-full text-center text-[11px] text-muted-foreground/70 hover:text-foreground py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      >
                        {showPast
                          ? "Hide past events"
                          : `Show ${pastEvents.length} past event${pastEvents.length === 1 ? "" : "s"}`}
                      </button>
                      {showPast && (
                        <div className="mt-1 space-y-1">
                          <div className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                            Past events
                          </div>
                          {renderGroups(pastEvents)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer: counts + global G/B/H chips */}
            <div className="px-3 py-2 border-t border-border bg-panel shrink-0 flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                {countLabel}
              </span>
              {summary && (
                <div className="flex items-center gap-1 font-mono text-[10px]">
                  <span className="px-1.5 py-0.5 rounded bg-green-500/15 text-green-500">
                    {summary.goodPhones}G
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-500">
                    {summary.badPhones}B
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                    {summary.heldCount}H
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
  );
}

function GlobalStatusContent({ summary }: { summary: DashboardSummary }) {
  return (
    <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 font-mono text-xs">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Inv</span>
        <span className="text-foreground">{summary.remainingInventory}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Hold</span>
        <span className="text-amber-500">{summary.heldCount}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Good</span>
        <span className="text-green-500">{summary.goodPhones}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Bad</span>
        <span className="text-red-500">{summary.badPhones}</span>
      </div>
    </div>
  );
}

function initials(label: string): string {
  const cleaned = label.replace(/[^a-zA-Z0-9 ]/g, " ").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parseEventDate(s?: string | null): Date | null {
  if (!s) return null;
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

// "Jun 12" for the expanded event row.
function formatMonthDay(s?: string | null): string | null {
  const d = parseEventDate(s);
  if (!d) return null;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// "JUN 12" pill under the collapsed avatar (em dash when no date).
function formatPill(s?: string | null): string {
  const d = parseEventDate(s);
  if (!d) return "—";
  return `${MONTHS[d.getMonth()].toUpperCase()} ${d.getDate()}`;
}
